import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { useSEO } from '@/hooks/useSEO';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import { formatCount, formatHeat } from '@/utils/format';

const toIso = (value: string) => value.replace(' ', 'T');

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  const related = useMemo(
    () =>
      topic
        ? topics.filter((item) => item.categorySlug === topic.categorySlug && item.id !== topic.id).slice(0, 3)
        : [],
    [topic]
  );

  const jsonLd = useMemo(() => {
    if (!topic) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.title,
        description: topic.summary,
        articleSection: topic.categoryName,
        keywords: topic.tags.join(','),
        author: { '@type': 'Organization', name: '黑料首页编辑部' },
        publisher: { '@type': 'Organization', name: '黑料首页' },
        datePublished: toIso(topic.publishedAt),
        dateModified: toIso(topic.updatedAt),
        inLanguage: 'zh-CN',
        mainEntityOfPage: `${origin}/topic/${topic.id}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin || undefined },
          { '@type': 'ListItem', position: 2, name: '话题库', item: `${origin}/topics` },
          { '@type': 'ListItem', position: 3, name: topic.categoryName, item: `${origin}/topics?category=${topic.categorySlug}` },
          { '@type': 'ListItem', position: 4, name: topic.title },
        ],
      },
    ];
  }, [topic]);

  useSEO({
    title: topic ? `${topic.title} | ${topic.categoryName} - 黑料首页` : '话题不存在 - 黑料首页',
    description: topic ? topic.summary : '该话题可能已被移除或链接有误。',
    keywords: topic ? `${topic.tags.join(',')},黑料首页,黑料首页最新` : '黑料首页',
    canonicalPath: topic ? `/topic/${topic.id}` : '/topics',
    type: 'article',
    jsonLd,
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-shell px-4 py-24 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-emotion-sad-line" aria-hidden="true" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这个话题暂时找不到了</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已被移除，或者链接输入有误。回到话题库看看其他内容吧。</p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回话题库
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((item) => item.slug === topic.categorySlug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '话题库', to: '/topics' },
                { label: topic.categoryName, to: `/topics?category=${topic.categorySlug}` },
                { label: '正文' },
              ]}
            />
            <div className="mt-5 max-w-4xl">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={`/topics?category=${topic.categorySlug}`}
                  className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900 hover:bg-secondary-200"
                >
                  {topic.categoryName}
                </Link>
                {topic.isBreaking && (
                  <span className="rounded-full bg-primary-500 px-2.5 py-0.5 font-medium text-background-50">最新</span>
                )}
                <span className="flex items-center gap-1 text-foreground-600">
                  <i className="ri-refresh-line" aria-hidden="true" />
                  最近更新 {topic.updatedAt}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
                {topic.title}
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">{topic.summary}</p>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-shell px-4 py-10 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.9fr_1fr]">
            <article className="min-w-0">
              <figure className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src={topic.cover}
                  alt={topic.coverAlt}
                  className="h-64 w-full object-cover object-top md:h-80"
                />
                <figcaption className="border-t border-background-200 bg-background-100 px-4 py-2 text-xs text-foreground-600">
                  配图为话题主题示意图，与具体人物无直接关联
                </figcaption>
              </figure>

              <div className="mt-8 flex flex-col gap-8">
                {topic.content.map((section, index) => (
                  <section key={section.heading} id={`sec-${index}`} className="scroll-mt-32">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
                    <div className="mt-3 flex flex-col gap-4">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph.slice(0, 24)} className="text-[15px] leading-8 text-foreground-800">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-600">相关标签：</span>
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-accent-200 bg-accent-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-error-warning-line text-accent-700" aria-hidden="true" />
                  内容说明
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  本文依据公开信息与网友讨论整理，以话题观察为目的，不构成对任何个人或机构的事实认定。如你对内容有异议，
                  欢迎通过
                  <Link to="/contact" className="mx-1 font-medium text-primary-700 underline">
                    联系页面
                  </Link>
                  提交说明，我们会核实并及时更正。
                </p>
              </div>
            </article>

            <aside className="flex flex-col gap-6 lg:sticky lg:top-32 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">话题概况</h2>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-foreground-600">热度</dt>
                    <dd className="mt-1 flex items-center gap-1 font-heading text-lg font-bold text-primary-700">
                      <i className="ri-fire-line" aria-hidden="true" />
                      {formatHeat(topic.heat)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">阅读</dt>
                    <dd className="mt-1 font-heading text-lg font-bold text-foreground-950">
                      {formatCount(topic.views)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">讨论</dt>
                    <dd className="mt-1 font-heading text-lg font-bold text-foreground-950">{topic.comments}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">发布时间</dt>
                    <dd className="mt-1 text-sm text-foreground-900">{topic.publishedAt.slice(5, 16)}</dd>
                  </div>
                </dl>
                <p className="mt-4 flex items-center gap-2 border-t border-background-200 pt-4 text-xs text-foreground-600">
                  <i className="ri-user-3-line" aria-hidden="true" />
                  {topic.author}
                </p>
              </div>

              <nav className="rounded-lg border border-background-200 bg-background-50 p-5" aria-label="本文目录">
                <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
                <ol className="mt-4 flex flex-col gap-2.5">
                  {topic.content.map((section, index) => (
                    <li key={section.heading} className="flex gap-2 text-sm">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background-100 text-[11px] font-bold text-foreground-700">
                        {index + 1}
                      </span>
                      <a href={`#sec-${index}`} className="leading-snug text-foreground-800 hover:text-primary-700">
                        {section.heading}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>

              {category && (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">所属栏目</h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{category.description}</p>
                  <Link
                    to={`/topics?category=${category.slug}`}
                    className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    浏览{category.name}
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-16" aria-labelledby="related-title">
              <div className="flex items-end justify-between gap-4">
                <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  同栏目相关话题
                </h2>
                <Link
                  to={`/topics?category=${topic.categorySlug}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
                >
                  更多
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <TopicCard key={item.id} topic={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}